import { useState, useRef, useCallback } from 'react';
import styled from '@emotion/styled';

interface InfoTipProps {
  text: string;
  placement?: 'top' | 'bottom';
  wide?: boolean;
}

const Wrap = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  cursor: help;
`;

const Icon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #e5e5e5;
  color: #626262;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;
  flex-shrink: 0;
  &:hover {
    background: #d3d3d3;
  }
`;

// Uses position:fixed so it escapes any overflow:hidden/auto ancestor (e.g. ContentScroll).
// Coordinates are set dynamically from getBoundingClientRect() on mouse-enter.
const Tooltip = styled.div<{ $x: number; $y: number; $wide: boolean; $placement: 'top' | 'bottom' }>`
  position: fixed;
  left: ${(p) => p.$x}px;
  top: ${(p) => p.$y}px;
  transform: translateX(-50%) ${(p) => (p.$placement === 'top' ? ' translateY(-100%)' : '')};
  width: ${(p) => (p.$wide ? '320px' : '260px')};
  background: #323232;
  color: #ffffff;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 8px;
  padding: 10px 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  z-index: 9999;
  pointer-events: none;
`;

const Arrow = styled.div<{ $placement: 'top' | 'bottom' }>`
  position: absolute;
  ${(p) =>
    p.$placement === 'bottom'
      ? 'bottom: 100%; border-bottom-color: #323232;'
      : 'top: 100%; border-top-color: #323232;'}
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
`;

export default function InfoTip({ text, placement = 'top', wide = false }: InfoTipProps): React.JSX.Element {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const handleMouseEnter = useCallback(() => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setPos({
      x: rect.left + rect.width / 2,
      y: placement === 'top' ? rect.top - 8 : rect.bottom + 8,
    });
  }, [placement]);

  const handleMouseLeave = useCallback(() => setPos(null), []);

  return (
    <Wrap ref={wrapRef} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Icon>?</Icon>
      {pos && (
        <Tooltip $x={pos.x} $y={pos.y} $wide={wide} $placement={placement}>
          {text}
          <Arrow $placement={placement} />
        </Tooltip>
      )}
    </Wrap>
  );
}
